import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, ArrowUp, ArrowDown, Trash2, GripVertical, Camera, MessageSquare, Wrench, ChevronLeft, ClipboardList,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const FREQS = [
  ["pre_arrival", "Pre-arrival"],
  ["daily", "Daily"],
  ["post_checkout", "Post-checkout"],
  ["every_x_hours", "Every X hours"],
  ["weekly", "Weekly"],
  ["monthly", "Monthly"],
];
const PRIOS = ["low", "medium", "high", "critical"];
const ROLES = ["housekeeping", "maintenance", "supervisor"];

const PRIORITY_TEXT_CLASS = {
  critical: "text-rose-600",
  high: "text-orange-600",
  medium: "text-zinc-600",
  low: "text-zinc-400",
};

// Quick-add chip suggestions — one click adds a fully-configured task using
// global defaults. The text comes from the chip; everything else inherits.
const SUGGESTED_TASKS = [
  "Make bed",
  "Clean bathroom",
  "Vacuum floor",
  "Replenish toiletries",
  "Check AC filter",
  "Wipe surfaces",
  "Restock minibar",
  "Inspect linens",
];

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const taskFromName = (name, defaults) => ({
  id: newId(),
  name: name.trim(),
  description: "",
  frequency: defaults.frequency,
  interval_hours: defaults.frequency === "every_x_hours" ? defaults.interval_hours || 2 : null,
  priority: defaults.priority,
  due_time: defaults.frequency === "every_x_hours" ? null : defaults.due_time,
  role: defaults.role,
  photo_required: defaults.photo_required,
  remarks_required: defaults.remarks_required,
  can_raise_ticket: defaults.can_raise_ticket,
});

export default function ChecklistEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { selectedId } = useProperty();

  const [zones, setZones] = useState([]);
  const [areas, setAreas] = useState([]);
  const [name, setName] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [items, setItems] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null); // null = show "Defaults" in inspector
  const [defaults, setDefaults] = useState({
    frequency: "daily", interval_hours: null, priority: "medium", due_time: "10:00",
    role: "housekeeping", photo_required: false, remarks_required: false, can_raise_ticket: true,
  });
  const [busy, setBusy] = useState(false);
  const [quickAdd, setQuickAdd] = useState("");
  const quickAddRef = useRef(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  // Bootstrap
  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      const [z, a] = await Promise.all([
        api.get(`/zones?property_id=${selectedId}`),
        api.get(`/areas?property_id=${selectedId}`),
      ]);
      setZones(z.data);
      setAreas(a.data);
      if (isEdit) {
        const { data } = await api.get(`/checklists?property_id=${selectedId}`);
        const found = data.find((c) => c.id === id);
        if (!found) {
          toast.error("Checklist not found");
          navigate("/checklists");
          return;
        }
        setName(found.name);
        setZoneId(found.zone_id || "");
        setAreaId(found.area_id || "");
        setItems(found.items || []);
      }
    })();
  }, [selectedId, id, isEdit, navigate]);

  const filteredAreas = useMemo(
    () => areas.filter((a) => !zoneId || a.zone_id === zoneId),
    [areas, zoneId]
  );

  const currentTask = selectedIdx != null ? items[selectedIdx] : null;

  // ----- Item ops -----
  const addTask = (taskName) => {
    const trimmed = (taskName || "").trim();
    if (!trimmed) return;
    setItems((prev) => [...prev, taskFromName(trimmed, defaults)]);
    setQuickAdd("");
    requestAnimationFrame(() => quickAddRef.current?.focus());
  };

  // Bulk-paste: convert a multi-line block into many checklist items, stripping
  // common bullet/numbering markers so users can dump from notes/sheets/docs.
  const parsePastedList = (raw) => {
    if (!raw) return [];
    const stripPatterns = [
      /^\s*[-*•·●◦▪▫▶▷→]+\s+/, // bullets
      /^\s*[–—]+\s+/,            // dashes
      /^\s*\(?\d+[.)\]]\s+/,     // 1. 1) 1] (1)
      /^\s*[a-zA-Z][.)]\s+/,     // a. b. a) b)
      /^\s*\[[ xX]?\]\s*/,       // [ ] [x] [X]
      /^\s*[☐☑✅✔︎✓✕✘]\s*/,    // unicode checkbox marks
    ];
    const seen = new Set(items.map((t) => t.name.trim().toLowerCase()));
    const out = [];
    raw.split(/\r?\n/).forEach((rawLine) => {
      let line = rawLine.replace(/\u00A0/g, " ").trim();
      if (!line) return;
      let changed = true;
      while (changed) {
        changed = false;
        for (const re of stripPatterns) {
          if (re.test(line)) {
            line = line.replace(re, "").trim();
            changed = true;
          }
        }
      }
      // Truncate very long lines so they fit the task name (UI looks bad otherwise)
      if (line.length > 200) line = line.slice(0, 200).trim();
      if (!line) return;
      const key = line.toLowerCase();
      if (seen.has(key)) return; // dedupe vs. existing items + within paste
      seen.add(key);
      out.push(line);
    });
    return out;
  };

  const addManyTasks = (names) => {
    if (!names || names.length === 0) return 0;
    setItems((prev) => [...prev, ...names.map((n) => taskFromName(n, defaults))]);
    return names.length;
  };

  const patchTask = (idx, patch) =>
    setItems((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));

  const removeTask = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    if (selectedIdx === idx) setSelectedIdx(null);
    else if (selectedIdx != null && selectedIdx > idx) setSelectedIdx(selectedIdx - 1);
  };

  const moveTask = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const copy = items.slice();
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    setItems(copy);
    if (selectedIdx === idx) setSelectedIdx(target);
    else if (selectedIdx === target) setSelectedIdx(idx);
  };

  const applyDefaultsToAll = () => {
    if (!window.confirm("Overwrite every task's settings with the current Defaults? Task names are kept.")) return;
    setItems((prev) => prev.map((t) => ({ ...t, ...taskFromName(t.name, defaults), id: t.id, description: t.description })));
    toast.success("Applied defaults to all tasks");
  };

  // ----- Save -----
  const save = async () => {
    if (!name.trim()) return toast.error("Give the checklist a name");
    if (!zoneId) return toast.error("Pick a zone");
    if (items.length === 0) return toast.error("Add at least one task");
    const missing = items.findIndex((t) => !t.name.trim());
    if (missing >= 0) {
      setSelectedIdx(missing);
      return toast.error("One task is missing a name");
    }
    setBusy(true);
    try {
      const payload = {
        property_id: selectedId,
        name: name.trim(),
        zone_id: zoneId,
        area_id: areaId || null,
        items,
      };
      if (isEdit) await api.put(`/checklists/${id}`, payload);
      else await api.post("/checklists", payload);
      toast.success(isEdit ? "Checklist updated" : "Checklist created");
      navigate("/checklists");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  if (!selectedId) return <div className="text-zinc-500">Select a property first.</div>;

  return (
    <div className="-mx-4 lg:-mx-8 -my-6 lg:-my-8 min-h-[calc(100vh-4rem)] flex flex-col">
      {/* Dark hero header — full bleed, sticky save CTA */}
      <header className="bg-zinc-950 text-white px-6 lg:px-12 py-8" data-testid="checklist-editor-header">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <button
            onClick={() => navigate("/checklists")}
            className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1"
            data-testid="back-to-checklists"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Checklists
          </button>
          <Button
            onClick={save}
            disabled={busy}
            className="bg-white text-zinc-900 hover:bg-zinc-100"
            data-testid="save-checklist-button"
          >
            {isEdit ? "Save changes" : "Create checklist"}
          </Button>
        </div>

        <div className="mt-6 max-w-3xl">
          <span className="label-overline text-zinc-500">{isEdit ? "Edit checklist" : "New checklist"}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name this checklist…"
            className="mt-2 w-full bg-transparent border-0 outline-none font-display text-4xl md:text-5xl font-black tracking-tighter placeholder:text-zinc-700 focus:placeholder:text-zinc-600 text-white"
            data-testid="checklist-name-input"
          />
        </div>

        {/* Zone + Area inline */}
        <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 mr-1">For</span>
          <Select value={zoneId} onValueChange={(v) => { setZoneId(v); setAreaId(""); }}>
            <SelectTrigger className="h-9 w-56 bg-zinc-900 border-zinc-800 text-white" data-testid="checklist-zone-select">
              <SelectValue placeholder="Pick a zone" />
            </SelectTrigger>
            <SelectContent>
              {zones.map((z) => (<SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <span className="text-zinc-600">·</span>
          <Select value={areaId} onValueChange={setAreaId} disabled={!zoneId}>
            <SelectTrigger className="h-9 w-56 bg-zinc-900 border-zinc-800 text-white" data-testid="checklist-area-select">
              <SelectValue placeholder="Whole zone (any area)" />
            </SelectTrigger>
            <SelectContent>
              {filteredAreas.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Two-pane body */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 bg-zinc-50">
        {/* LEFT — Outline */}
        <section className="lg:col-span-3 border-r border-zinc-200 bg-white p-6 lg:p-10">
          <div className="flex items-center justify-between mb-4">
            <span className="label-overline">Tasks ({items.length})</span>
            {items.length > 0 && (
              <button
                onClick={() => setSelectedIdx(null)}
                className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-zinc-900"
                data-testid="open-defaults"
              >
                Defaults &amp; rules →
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-md overflow-hidden">
              {items.map((it, idx) => (
                <TaskRow
                  key={it.id}
                  task={it}
                  idx={idx}
                  total={items.length}
                  selected={selectedIdx === idx}
                  onSelect={() => setSelectedIdx(idx)}
                  onRename={(v) => patchTask(idx, { name: v })}
                  onRemove={() => removeTask(idx)}
                  onMoveUp={() => moveTask(idx, -1)}
                  onMoveDown={() => moveTask(idx, +1)}
                />
              ))}
            </div>
          )}

          {/* Quick-add — Enter to add, focus keeps on input */}
          <div className="mt-4 flex items-center gap-2">
            <div className="flex-1 relative">
              <Plus className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                ref={quickAddRef}
                value={quickAdd}
                onChange={(e) => setQuickAdd(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(quickAdd); } }}
                placeholder="Type a task and hit Enter…"
                className="pl-9 h-11"
                data-testid="add-task-input"
              />
            </div>
            <Button
              onClick={() => addTask(quickAdd)}
              disabled={!quickAdd.trim()}
              className="bg-zinc-950 hover:bg-zinc-800 h-11"
              data-testid="add-task-button"
            >
              Add
            </Button>
            <Button
              variant="outline"
              onClick={() => { setPasteText(""); setPasteOpen(true); }}
              className="h-11"
              title="Paste a multi-line list and convert each line into a task"
              data-testid="paste-list-button"
            >
              <ClipboardList className="w-4 h-4 mr-1" /> Paste list
            </Button>
          </div>

          {/* Suggested tasks */}
          <div className="mt-4">
            <span className="text-[11px] uppercase tracking-widest text-zinc-400">Or pick a common one</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {SUGGESTED_TASKS.map((s) => (
                <button
                  key={s}
                  onClick={() => addTask(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white transition"
                  data-testid={`suggested-${s.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* RIGHT — Inspector */}
        <aside className="lg:col-span-2 p-6 lg:p-10">
          <div className="lg:sticky lg:top-6">
            {currentTask ? (
              <Inspector
                key={currentTask.id}
                task={currentTask}
                onChange={(p) => patchTask(selectedIdx, p)}
                onClose={() => setSelectedIdx(null)}
                idxLabel={`Task ${selectedIdx + 1} of ${items.length}`}
              />
            ) : (
              <DefaultsPanel
                defaults={defaults}
                onChange={setDefaults}
                onApplyAll={applyDefaultsToAll}
                hasItems={items.length > 0}
              />
            )}
          </div>
        </aside>
      </div>

      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="max-w-xl" data-testid="paste-list-dialog">
          <DialogHeader>
            <DialogTitle>Paste a list of tasks</DialogTitle>
            <DialogDescription>
              One task per line. Bullets like <code>-</code>, <code>*</code>, <code>1.</code>, <code>[ ]</code> are stripped automatically.
              Each line uses the current Defaults (frequency, priority, role…).
              Duplicates of existing tasks are skipped.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={12}
              placeholder={"e.g.\n- Make bed\n- Clean bathroom mirror\n[ ] Restock toiletries\n1. Vacuum carpet\n2. Wipe AC vents"}
              className="font-mono text-xs"
              data-testid="paste-list-textarea"
              autoFocus
            />
            <PreviewSummary text={pasteText} parser={parsePastedList} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasteOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                const parsed = parsePastedList(pasteText);
                if (parsed.length === 0) { toast.error("No tasks detected. Paste one task per line."); return; }
                addManyTasks(parsed);
                toast.success(`Added ${parsed.length} task${parsed.length === 1 ? "" : "s"}`);
                setPasteOpen(false);
                setPasteText("");
              }}
              className="bg-zinc-950 hover:bg-zinc-800"
              data-testid="paste-list-add"
            >
              <Plus className="w-4 h-4 mr-1" /> Add tasks
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewSummary({ text, parser }) {
  const parsed = parser(text);
  if (!text.trim()) {
    return <p className="text-[11px] text-zinc-500">0 tasks detected.</p>;
  }
  return (
    <div className="text-[11px] text-zinc-600">
      <div data-testid="paste-list-preview-count">
        Detected <strong className="text-zinc-900">{parsed.length}</strong> task{parsed.length === 1 ? "" : "s"} after stripping bullets &amp; dedupe.
      </div>
      {parsed.length > 0 && (
        <ul className="mt-1.5 max-h-32 overflow-auto border border-zinc-200 rounded px-2 py-1 bg-zinc-50">
          {parsed.slice(0, 8).map((p, i) => (
            <li key={`${i}-${p}`} className="truncate">{i + 1}. {p}</li>
          ))}
          {parsed.length > 8 && <li className="text-zinc-400">…and {parsed.length - 8} more</li>}
        </ul>
      )}
    </div>
  );
}

// ---------------- Task Row ----------------
function TaskRow({ task, idx, total, selected, onSelect, onRename, onRemove, onMoveUp, onMoveDown }) {
  const [editing, setEditing] = useState(false);
  return (
    <div
      onClick={onSelect}
      data-selected={selected ? "true" : "false"}
      className={`group flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors ${
        selected ? "bg-zinc-100 border-l-4 border-l-zinc-900 pl-2" : "hover:bg-zinc-50 border-l-4 border-l-transparent pl-2"
      }`}
      data-testid={`task-row-${idx}`}
    >
      <GripVertical className="w-4 h-4 text-zinc-300 group-hover:text-zinc-400 shrink-0" />
      <span className="text-[10px] font-mono text-zinc-400 w-6">{(idx + 1).toString().padStart(2, "0")}</span>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={task.name}
            onChange={(e) => onRename(e.target.value)}
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setEditing(false); }}
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 outline-none text-sm font-medium"
            data-testid={`task-name-input-${idx}`}
          />
        ) : (
          <div
            onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
            className={`text-sm font-medium truncate ${task.name ? "text-zinc-900" : "text-rose-600 italic"}`}
          >
            {task.name || "Unnamed task"}
          </div>
        )}
        <TaskMeta task={task} />
      </div>

      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 shrink-0 transition">
        <IconBtn label="Move up" disabled={idx === 0} onClick={(e) => { e.stopPropagation(); onMoveUp(); }} testid={`move-up-${idx}`}><ArrowUp className="w-3.5 h-3.5" /></IconBtn>
        <IconBtn label="Move down" disabled={idx === total - 1} onClick={(e) => { e.stopPropagation(); onMoveDown(); }} testid={`move-down-${idx}`}><ArrowDown className="w-3.5 h-3.5" /></IconBtn>
        <IconBtn label="Delete" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="hover:bg-rose-50 hover:text-rose-600" testid={`remove-task-${idx}`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
      </div>
    </div>
  );
}

function TaskMeta({ task }) {
  const freqLabel = FREQS.find(([k]) => k === task.frequency)?.[1] || task.frequency;
  return (
    <div className="flex items-center gap-2 mt-0.5 text-[10px] uppercase tracking-widest text-zinc-500">
      <span>{task.role}</span>
      <span className="text-zinc-300">·</span>
      <span>{freqLabel}{task.frequency === "every_x_hours" && task.interval_hours ? ` (${task.interval_hours}h)` : ""}</span>
      {task.due_time && task.frequency !== "every_x_hours" && (
        <>
          <span className="text-zinc-300">·</span>
          <span>{task.due_time}</span>
        </>
      )}
      <span className="text-zinc-300">·</span>
      <span className={PRIORITY_TEXT_CLASS[task.priority] || "text-zinc-400"}>{task.priority}</span>
      {task.photo_required && <Camera className="w-3 h-3 text-zinc-400" title="Photo required" />}
      {task.remarks_required && <MessageSquare className="w-3 h-3 text-zinc-400" title="Remarks required" />}
      {task.can_raise_ticket && <Wrench className="w-3 h-3 text-zinc-400" title="Can raise ticket" />}
    </div>
  );
}

function IconBtn({ children, onClick, disabled, className = "", testid, label }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      data-testid={testid}
      className={`p-1.5 rounded hover:bg-zinc-200 disabled:opacity-30 disabled:hover:bg-transparent ${className}`}
    >
      {children}
    </button>
  );
}

// ---------------- Inspector (single task) ----------------
function Inspector({ task, onChange, onClose, idxLabel }) {
  return (
    <div className="border border-zinc-200 rounded-xl bg-white shadow-sm p-6 flex flex-col gap-5" data-testid="inspector-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="label-overline">{idxLabel}</span>
          <h3 className="font-display text-xl font-black tracking-tight mt-1 truncate">{task.name || "Unnamed task"}</h3>
        </div>
        <button onClick={onClose} className="text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-900 shrink-0" data-testid="inspector-close">Defaults →</button>
      </div>

      <Field label="Description">
        <Textarea
          rows={2}
          value={task.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Notes for the team member who runs this…"
          data-testid="inspector-description"
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Assignee">
          <Select value={task.role} onValueChange={(v) => onChange({ role: v })}>
            <SelectTrigger data-testid="inspector-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
          </Select>
        </Field>
        <Field label="Priority">
          <Select value={task.priority} onValueChange={(v) => onChange({ priority: v })}>
            <SelectTrigger data-testid="inspector-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIOS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="Happens">
        <Select
          value={task.frequency}
          onValueChange={(v) =>
            onChange({
              frequency: v,
              interval_hours: v === "every_x_hours" ? task.interval_hours || 2 : null,
              due_time: v === "every_x_hours" ? null : task.due_time || "10:00",
            })
          }
        >
          <SelectTrigger data-testid="inspector-frequency-select"><SelectValue /></SelectTrigger>
          <SelectContent>{FREQS.map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}</SelectContent>
        </Select>
      </Field>

      {task.frequency === "every_x_hours" ? (
        <Field label="Every (hours)">
          <Input
            type="number" min="1" max="24"
            value={task.interval_hours ?? ""}
            onChange={(e) => onChange({ interval_hours: e.target.value ? Number(e.target.value) : null })}
            data-testid="inspector-interval"
          />
        </Field>
      ) : (
        <Field label="Due by (time of day)">
          <Input
            type="time"
            value={task.due_time || ""}
            onChange={(e) => onChange({ due_time: e.target.value })}
            data-testid="inspector-due-time"
          />
        </Field>
      )}

      <div className="border-t border-zinc-100 pt-4">
        <span className="label-overline">Verification &amp; rules</span>
        <div className="mt-3 space-y-2.5">
          <RuleCheckbox
            checked={task.photo_required}
            onChange={(v) => onChange({ photo_required: v })}
            icon={Camera}
            label="Photo required"
            sub="Force the team member to upload a photo on completion."
            testid="rule-photo"
          />
          <RuleCheckbox
            checked={task.remarks_required}
            onChange={(v) => onChange({ remarks_required: v })}
            icon={MessageSquare}
            label="Remarks required"
            sub="Must write a short note when ticking off."
            testid="rule-remarks"
          />
          <RuleCheckbox
            checked={task.can_raise_ticket}
            onChange={(v) => onChange({ can_raise_ticket: v })}
            icon={Wrench}
            label="Can raise a maintenance ticket"
            sub="If they spot a problem during this task, they can flag it."
            testid="rule-ticket"
          />
        </div>
      </div>
    </div>
  );
}

// ---------------- Defaults / Inspector idle state ----------------
function DefaultsPanel({ defaults, onChange, onApplyAll, hasItems }) {
  const set = (p) => onChange({ ...defaults, ...p });
  return (
    <div className="border border-zinc-200 rounded-xl bg-white shadow-sm p-6 flex flex-col gap-5" data-testid="defaults-panel">
      <div>
        <span className="label-overline">Defaults</span>
        <h3 className="font-display text-xl font-black tracking-tight mt-1">New tasks inherit these.</h3>
        <p className="text-sm text-zinc-500 mt-1.5">
          Set them once, then add tasks rapidly below. Click any task to override its specifics.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Assignee">
          <Select value={defaults.role} onValueChange={(v) => set({ role: v })}>
            <SelectTrigger data-testid="default-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}</SelectContent>
          </Select>
        </Field>
        <Field label="Priority">
          <Select value={defaults.priority} onValueChange={(v) => set({ priority: v })}>
            <SelectTrigger data-testid="default-priority"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIOS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="Happens">
        <Select
          value={defaults.frequency}
          onValueChange={(v) => set({
            frequency: v,
            interval_hours: v === "every_x_hours" ? defaults.interval_hours || 2 : null,
            due_time: v === "every_x_hours" ? null : defaults.due_time || "10:00",
          })}
        >
          <SelectTrigger data-testid="default-frequency"><SelectValue /></SelectTrigger>
          <SelectContent>{FREQS.map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}</SelectContent>
        </Select>
      </Field>

      {defaults.frequency === "every_x_hours" ? (
        <Field label="Every (hours)">
          <Input type="number" min="1" max="24" value={defaults.interval_hours ?? ""}
            onChange={(e) => set({ interval_hours: e.target.value ? Number(e.target.value) : null })}
            data-testid="default-interval" />
        </Field>
      ) : (
        <Field label="Due by (time of day)">
          <Input type="time" value={defaults.due_time || ""} onChange={(e) => set({ due_time: e.target.value })} data-testid="default-time" />
        </Field>
      )}

      <div className="border-t border-zinc-100 pt-4">
        <span className="label-overline">Default rules</span>
        <div className="mt-3 space-y-2.5">
          <RuleCheckbox checked={defaults.photo_required} onChange={(v) => set({ photo_required: v })} icon={Camera} label="Photo required" testid="default-rule-photo" />
          <RuleCheckbox checked={defaults.remarks_required} onChange={(v) => set({ remarks_required: v })} icon={MessageSquare} label="Remarks required" testid="default-rule-remarks" />
          <RuleCheckbox checked={defaults.can_raise_ticket} onChange={(v) => set({ can_raise_ticket: v })} icon={Wrench} label="Can raise ticket" testid="default-rule-ticket" />
        </div>
      </div>

      {hasItems && (
        <Button variant="outline" onClick={onApplyAll} className="w-full" data-testid="apply-defaults-all">
          Apply these to ALL existing tasks
        </Button>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function RuleCheckbox({ checked, onChange, icon: Icon, label, sub, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group" data-testid={testid}>
      <Checkbox checked={checked} onCheckedChange={onChange} className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-sm font-medium text-zinc-900">
          <Icon className="w-3.5 h-3.5 text-zinc-500" />
          {label}
        </div>
        {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
      </div>
    </label>
  );
}

function EmptyState() {
  return (
    <div className="border-2 border-dashed border-zinc-200 rounded-md py-16 text-center" data-testid="checklist-empty">
      <div className="text-5xl mb-3 opacity-20" aria-hidden>✓</div>
      <h3 className="font-display text-lg font-bold tracking-tight">No tasks yet</h3>
      <p className="text-sm text-zinc-500 mt-1 max-w-xs mx-auto">
        Type a task name below and hit Enter — that&apos;s it. Tweak details later.
      </p>
    </div>
  );
}
